import { useState, useEffect, useCallback } from "react";
import useNotify from "../../hooks/notify";
import useUser from "../../hooks/user/useUser";
import OrderService from "../../services/orderService";
import useAuthorize from "../../hooks/user/useAuthorize";
import { Permissions } from "../../models/Roles";
import { CanPrint } from "../../models/OrderEntity";
import BranchService from "../../services/branchService";

export default function useVoucherDetails
    ({ order, autoPrintInvoice }) {
    const { Notify, variants } = useNotify();
    const { branch } = useUser();
    const isAuthorized = useAuthorize();

    // Estados
    const [btnRefPrint, setBtnRefPrint] = useState(null);
    const [btnRefSendMail, setBtnRefSendMail] = useState(null);
    const [voucherSelected, setVoucherSelected] = useState(null);
    const [showInvoicePrintModal, setShowInvoicePrintModal] = useState(false);
    const [showChangeTicketPrintModal, setShowChangeTicketPrintModal] = useState(false);
    const [sendEmailChange, setSendEmailChange] = useState(false);
    const [showSendEmailModal, setShowSendEmailModal] = useState(false);
    const [isChange, setIsChange] = useState(false);

    // Print/Email
    const [printerId, setPrinterId] = useState();
    const [invoice, setInvoice] = useState("");
    const [isTaxFree, setIsTaxFree] = useState(false);
    const [loadingPrintModal, setLoadingPrintModal] = useState(false);
    const [loadingChangeTicketPrintModal, setLoadingChangeTicketPrintModal] = useState(false);
    const [loadingModalSendEmail, setLoadingModalSendEmail] = useState(false);

    // Change ticket
    const [changeTicket, setChangeTicket] = useState();
    const [copies, setCopies] = useState(1);

    // Para ModalSendEmail
    const [emailData, setEmailData] = useState({
        voucherId: null,
        changeItems: [],
        email: order?.customer?.email || "",
        isTaxFree: false,
        isChangeTicket: false,
    });

    // Flags de autorización y permisos de impresión
    const isAuthorizedPrint = isAuthorized([Permissions.SalesPrint]);
    const canPrint = CanPrint(order, branch?.code);

    // Efecto para autoPrintInvoice
    useEffect(() => {
        if (autoPrintInvoice) {
            setVoucherSelected(autoPrintInvoice);
            setShowInvoicePrintModal(true);
        }
    }, [autoPrintInvoice]);

    // Efecto para cargar invoice cuando cambia voucherSelected y showInvoicePrintModal
    useEffect(() => {
        if (voucherSelected && showInvoicePrintModal) {
            setLoadingPrintModal(true);
            OrderService.GetTicket(voucherSelected.id, voucherSelected.type)
                .then(res => setInvoice(res))
                .catch(() => Notify("Error al intentar obtener factura", variants.ERROR))
                .finally(() => setLoadingPrintModal(false));
        }
    }, [voucherSelected, showInvoicePrintModal, Notify, variants.ERROR]);

    // Efecto para cargar changeTicket cuando cambia voucherSelected y showChangeTicketPrintModal
    useEffect(() => {
        if (voucherSelected && showChangeTicketPrintModal) {
            setLoadingChangeTicketPrintModal(true);
            OrderService.GetChangeTicket(voucherSelected.id, [])
                .then(res => {
                    setChangeTicket(res)
                })
                .catch(() => Notify("Error al intentar obtener ticket de cambio", variants.ERROR))
                .finally(() => setLoadingChangeTicketPrintModal(false));
        }
    }, [voucherSelected, showChangeTicketPrintModal, Notify, variants.ERROR]);

    // Efecto para obtener impresoras
    const [printers, setPrinters] = useState([]);
    useEffect(() => {
        if (!branch?.id) return;
        BranchService.GetPrinters(branch.id)
            .then((res = []) => {
                const withOutFiscal = res.filter(printer => printer.isFiscal === false);
                setPrinters(withOutFiscal);
            })
            .catch(() => Notify("Error al intentar obtener impresoras", variants.ERROR));
    }, [branch?.id, Notify, variants.ERROR]);

    // Métodos
    const handlePrint = useCallback((voucher) => {
        setVoucherSelected(voucher);
        setShowInvoicePrintModal(true);
    }, []);


    const handlePrintChange = useCallback((voucher) => {
        setVoucherSelected(voucher);
        setShowChangeTicketPrintModal(true);
    }, []);

    const handleSendMail = useCallback((voucher) => {
        setIsChange(false);
        setVoucherSelected(voucher);
        setShowSendEmailModal(true);
        setEmailData({
            ...emailData,
            voucherId: voucher.id,
            email: order?.customer?.email || "",
            isChangeTicket: false,
        });
    }, [order, emailData]);

    const handleSendMailChange = useCallback((voucher) => {
        setIsChange(true);
        setVoucherSelected(voucher);
        setSendEmailChange(true);
        setShowChangeTicketPrintModal(true);
        setShowSendEmailModal(true);
        setEmailData({
            ...emailData,
            voucherId: voucher.id,
            email: order?.customer?.email || "",
            isChangeTicket: true,
        });
    }, [order, emailData]);

    const handleClose = useCallback(() => {
        setVoucherSelected(null);
        setShowInvoicePrintModal(false);
        setShowChangeTicketPrintModal(false);
        setShowSendEmailModal(false);
        setSendEmailChange(false);
        setIsChange(false);
        setInvoice("");
        setChangeTicket(null);
        setPrinterId(null);
        setIsTaxFree(false);
        setCopies(1);
    }, []);

    // Métodos para impresión
    const printInvoice = async () => {
        if (!voucherSelected) return;
        setLoadingPrintModal(true);
        try {
            await OrderService.PrintTicket(voucherSelected.id, voucherSelected.type, printerId, isTaxFree);
            Notify("Se ha impreso correctamente", variants.SUCCESS);
        } catch {
            Notify("Error al intentar imprimir ticket", variants.ERROR);
        } finally {
            setLoadingPrintModal(false);
            handleClose();
        }
    };

    // Métodos para change ticket
    const handlerOnAccept = async () => {
        setLoadingChangeTicketPrintModal(true);
        try {
            const res = await OrderService.GetChangeTicket(voucherSelected.id, []);
            setChangeTicket(res);
        } catch {
            Notify("Error al intentar obtener ticket de cambio", variants.ERROR);
        } finally {
            setLoadingChangeTicketPrintModal(false);
        }
    };


    const handlePrintChangeTicket = async () => {
        setLoadingChangeTicketPrintModal(true);
        try {
            await OrderService.PrintChangeTicket(voucherSelected.id, printerId, [], copies);
            Notify("Ticket de cambio impreso", variants.SUCCESS);
            handleClose();
        } catch {
            Notify("Error al intentar imprimir ticket de cambio", variants.ERROR);
        } finally {
            setLoadingChangeTicketPrintModal(false);
        }
    };

    // Métodos para email
    const handleValueChange = (value, name) => {
        setEmailData({
            ...emailData,
            [name]: value,
        });
    };

    const handleSwitchChange = (event) => {
        setEmailData({
            ...emailData,
            [event.target.name]: event.target.checked,
        });
        if (event.target.name === "isTaxFree") setIsTaxFree(event.target.checked);
    };

    const sendEmail = async () => {
        setLoadingModalSendEmail(true);
        const services = isChange
            ? OrderService.SendEmailChangeTicket
            : OrderService.SendEmailTicket;
        try {
            await services(emailData);
            Notify(
                `Se ha enviado el email con ${isChange ? "el ticket de cambio" : "la factura"}`,
                variants.SUCCESS
            );
            handleClose();
        } catch {
            Notify(
                `Error al intentar enviar el email con ${isChange ? "el ticket de cambio" : "la factura"}`,
                variants.ERROR
            );
        } finally {
            setLoadingModalSendEmail(false);
        }
    };

    // Selección de impresora
    const handleSelectPrinter = (printerId) => {
        setPrinterId(printerId);
    };

    return {
        // Estados y handlers para pasar a VoucherDetails
        order,
        voucher: voucherSelected,
        openInvoicePrintModal: showInvoicePrintModal,
        showChangeTicketPrintModal,
        isAuthorized: isAuthorizedPrint,
        canPrint,
        printInvoice,
        handleSwitchChange,
        handlerOnAccept,
        handleSelectPrinter,
        printers,
        loadingPrintModal,
        printerId,
        handleValueChange,
        btnRefPrint,
        emailData,
        setBtnRefPrint,
        setBtnRefSendMail,
        btnRefSendMail,
        invoice,
        isTaxFree,
        isSendMail: sendEmailChange,
        sendEmail,
        loadingChangeTicketPrintModal,
        showSendEmailModal,
        handlePrintChangeTicket,
        loadingModalSendEmail,
        changeTicket,
        voucherId: voucherSelected?.id,
        isChange,
        handleClose,
        handlePrintChange,
        handleSendMailChange,
        handleSendMail,
        handlePrint,
        branch,
        copies,
        onCopiesChange: setCopies,
    };
}
